import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { collectionToStories, Link } from "@quintype/components";
import { CollectionName } from "../../Atoms/CollectionName";
import { StoryCard } from "../../Molecules/StoryCard";
import { generateNavigateSlug, getSlug, getTextColor, navigateTo } from "../../../utils/utils";
import { getTemplateName } from "../../../../components/utils";
import "./four-col-slider.m.css";
import { Arrow } from "../../Svgs/arrow";
import AuthorImage from "../../Atoms/AuthorImage";
import { LoadmoreButton } from "../../Atoms/Loadmore";
import { useDispatch } from "react-redux";
import { SvgIconHandler } from "../../../../components/atoms";
import { useDeviceType } from "../../../../components/customHooks/useDeviceType";
import { getCategoryAndSubCategory } from "../../../../ga-utils";

const FourColSlider = ({ collection, config = {}, page = "" }) => {
  const [currentPosition, setCurrentPosition] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);
  const carouselRef = useRef(null);
  const dispatch = useDispatch();
  const [cardsPerView, setCardsPerView] = useState(4);

  const url = generateNavigateSlug(collection, {});

  const {
    theme = "",
    collectionNameTemplate = "",
    collectionNameBorderColor = "",
    aspectRatio = [
      [16, 9],
      [16, 9],
    ],
  } = config;

  const storyItems = collectionToStories(collection);
  const textColor = getTextColor(theme);

  if (!storyItems.length) return null;

  useEffect(() => {
    const updateCardsPerView = () => {
      const carousel = carouselRef.current;
      if (carousel && carousel.children.length > 0) {
        const containerWidth = carousel.offsetWidth;
        const cardWidth = carousel.children[0].offsetWidth;
        setCardsPerView(Math.max(1, Math.floor(containerWidth / cardWidth)));
      }
    };
    updateCardsPerView();
    window.addEventListener("resize", updateCardsPerView);
    return () => window.removeEventListener("resize", updateCardsPerView);
  }, [storyItems]);

  useEffect(() => {
    const updateMaxScroll = () => {
      const carousel = carouselRef.current;
      if (carousel) {
        setMaxScroll(carousel.scrollWidth - carousel.clientWidth);
      }
    };

    updateMaxScroll();
    window.addEventListener("resize", updateMaxScroll);
    return () => window.removeEventListener("resize", updateMaxScroll);
  }, [storyItems]);

  const handleScroll = () => {
    const carousel = carouselRef.current;
    if (carousel) {
      setCurrentPosition(carousel.scrollLeft);
    }
  };

  useEffect(() => {
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener("scroll", handleScroll);
      return () => carousel.removeEventListener("scroll", handleScroll);
    }
  }, []);

  // Helper to scroll to a specific card index
  const scrollToCard = (cardIndex) => {
    const carousel = carouselRef.current;
    if (carousel && carousel.children[cardIndex]) {
      carousel.scrollTo({
        left: carousel.children[cardIndex].offsetLeft,
        behavior: "smooth",
      });
    }
  };

  const scrollLeft = () => {
    const carousel = carouselRef.current;
    if (carousel) {
      const children = Array.from(carousel.children);
      const currentScroll = carousel.scrollLeft;
      let firstVisible = 0;
      for (let i = 0; i < children.length; i++) {
        if (children[i].offsetLeft + children[i].offsetWidth > currentScroll) {
          firstVisible = i;
          break;
        }
      }
      scrollToCard(Math.max(0, firstVisible - 1));
    }
  };

  const scrollRight = () => {
    const carousel = carouselRef.current;
    if (carousel) {
      const children = Array.from(carousel.children);
      const currentScroll = carousel.scrollLeft;
      let firstVisible = 0;
      for (let i = 0; i < children.length; i++) {
        if (children[i].offsetLeft + children[i].offsetWidth > currentScroll) {
          firstVisible = i;
          break;
        }
      }
      scrollToCard(Math.min(children.length - 1, firstVisible + 1));
    }
  };

  const renderStoryCard = (story, headerLevel = "3") => (
    <>
      <div styleName="author-info">
        <AuthorImage author={story.authors[0]} isClickable={true} />
        <Link href={`/author/${story.authors[0].slug}`}>
          <div styleName="author-name">{story.authors[0].name}</div>
        </Link>
      </div>
      <StoryCard story={story} headerLevel={headerLevel} aspectRatio={aspectRatio} config={config} />
    </>
  );

  const getLoadMore = () => {
    return (
      <div onClick={handleReadMoreClick} style={{ cursor: "pointer" }}>
        <LoadmoreButton
          config={config}
          collection={collection}
          sectionSwitch={false}
          qtConfig={{}}
          isBtnWithArrow={true}
        />
      </div>
    );
  };

  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";

  const pushDataLayerEvent = (position) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, page);
      window.dataLayer.push({
        event: "dl_story_listing_component_click",
        link_position: position,
        section_name: collection?.name?.toLowerCase(),
        category: category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: getTemplateName(page),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  const handleCollectionNameClick = () => {
    pushDataLayerEvent(-2);
    navigateTo(dispatch, url);
  };

  const handleReadMoreClick = () => {
    pushDataLayerEvent(-1);
    navigateTo(dispatch, url);
  };

  const handleStoryClick = (position) => {
    pushDataLayerEvent(position + 1); // +1 to convert from 0-indexed to 1-indexed
  };

  return (
    <div>
      <div
        className="full-width-with-padding arrow-component"
        data-test-id="four-col-slider"
        styleName="wrapper"
        style={{ backgroundColor: theme, color: textColor }}
      >
        <div styleName="four-col-slider-wrapper">
          <div styleName="collection-name-with-read-more" onClick={handleCollectionNameClick}>
            <CollectionName
              collection={collection}
              collectionNameTemplate={collectionNameTemplate}
              collectionNameBorderColor={collectionNameBorderColor}
            />
            {isDesktop ? (
              page !== "author-page" && page !== "profile-page" && getLoadMore()
            ) : (
              <Link className="load-more-button" href={getSlug(collection, {})}>
                <SvgIconHandler type="read-more-arrow" width="18" height="18" viewBox="0 0 18 18" />
              </Link>
            )}
          </div>

          <div styleName="slider-container">
            <div styleName="mobile-cards">
              {storyItems.map((story, index) => (
                <div key={story.id} styleName="mobile-card" onClick={() => handleStoryClick(index)}>
                  {renderStoryCard(story)}
                </div>
              ))}
            </div>

            <div styleName="desktop-cards" ref={carouselRef}>
              {storyItems.map((story, index) => (
                <div key={story.id} styleName="desktop-card" onClick={() => handleStoryClick(index)}>
                  {renderStoryCard(story)}
                </div>
              ))}
            </div>

            {/* Slider tracker line and navigation arrows (desktop only) */}
            <div styleName="slider-tracker-nav-row">
              <button
                type="button"
                styleName={`slider-arrow left${currentPosition <= 0 ? " disabled" : ""}`}
                onClick={scrollLeft}
                disabled={currentPosition <= 0}
                aria-label="Scroll left"
              >
                <Arrow color={`${currentPosition <= 0 ? "#000000" : "#FFFFFF"}`} />
              </button>
              <div styleName="slider-tracker">
                <div
                  styleName="slider-tracker-bar"
                  style={{
                    width: "25%",
                    left: `${
                      storyItems.length <= cardsPerView ? 0 : Math.min(75, (currentPosition / maxScroll) * 75)
                    }%`,
                  }}
                />
              </div>
              <button
                type="button"
                styleName={`slider-arrow right${currentPosition >= maxScroll - 1 ? " disabled" : ""}`}
                onClick={scrollRight}
                disabled={currentPosition >= maxScroll - 1}
                aria-label="Scroll right"
              >
                <Arrow color={`${currentPosition >= maxScroll - 1 ? "#000000" : "#FFFFFF"}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

FourColSlider.propTypes = {
  collection: PropTypes.object.isRequired,
  config: PropTypes.shape({
    theme: PropTypes.string,
    collectionNameTemplate: PropTypes.string,
    collectionNameBorderColor: PropTypes.string,
    navigationArrows: PropTypes.bool,
    aspectRatio: PropTypes.array,
  }),
  page: PropTypes.string,
};

export default FourColSlider;

FourColSlider.storyLimit = 12;
